import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormCheckbox, FormGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';

export const SuspendedWorkboxDesignDetails = React.forwardRef((props, ref) => {
  const {
    maxRatedCapacity: savedMaxRatedCapacity = '',
    maxPersons: savedPersons = '',
    tareMass: savedTareMass = '',
    boxLength: savedBoxLength = '',
    boxWidth: savedBoxWidth = '',
    boxHeight: savedBoxHeight = '',
    isForFirstAidOnly: savedIsForFirstAidOnly = '',
  } = props.value || {};

  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [maxPersons, setPersons] = useState(savedPersons);
  const [tareMass, setMaxTrave] = useState(savedTareMass);
  const [boxLength, setBoxLength] = useState(savedBoxLength);
  const [boxWidth, setBoxWidth] = useState(savedBoxWidth);
  const [boxHeight, setBoxHeight] = useState(savedBoxHeight);
  const [isForFirstAidOnly, setIsForFirstAidOnly] = useState(savedIsForFirstAidOnly);

  const [showErrors, setShowErrors] = useState(false);

  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const PAGE_CONSTANTS = PlantDesignDetailsPage.suspendedWorkbox;

  const validators = [
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxPersons',
      fieldValue: maxPersons,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'tareMass',
      fieldValue: tareMass,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'boxLength',
      fieldValue: boxLength,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'boxWidth',
      fieldValue: boxWidth,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'boxHeight',
      fieldValue: boxHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      maxRatedCapacity,
      maxPersons,
      tareMass,
      boxLength,
      boxWidth,
      boxHeight,
      isForFirstAidOnly,
    };

    if (props.onChange) props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [maxRatedCapacity, maxPersons, tareMass, boxLength, boxWidth, boxHeight, isForFirstAidOnly]);

  useEffect(() => {
    if (props.onErrorChange) props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormGroup
        id="maxRatedCapacity"
        label={PAGE_CONSTANTS.maxRatedCapacity.label}
        isRequired
        errorMessage={errors?.maxRatedCapacity?.errorMessage}
        hasError={showErrors && errors?.maxRatedCapacity?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxRatedCapacity.suffix}
          onChange={(e) => {
            setMaxRatedCapacity(e.target.value);
          }}
          value={maxRatedCapacity}
        />
      </FormGroup>

      <FormGroup
        id="maxPersons"
        label={PAGE_CONSTANTS.maxPersons.label}
        isRequired
        errorMessage={errors?.maxPersons?.errorMessage}
        hasError={showErrors && errors?.maxPersons?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          onChange={(e) => {
            setPersons(e.target.value);
          }}
          value={maxPersons}
          inputWidth="sm"
        />
      </FormGroup>
      <FormGroup
        id="tareMass"
        label={PAGE_CONSTANTS.tareMass.label}
        isRequired
        errorMessage={errors?.tareMass?.errorMessage}
        hasError={showErrors && errors?.tareMass?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.tareMass.suffix}
          onChange={(e) => {
            setMaxTrave(e.target.value);
          }}
          value={tareMass}
        />
      </FormGroup>
      <FormGroup
        id="boxLength"
        label={PAGE_CONSTANTS.boxLength.label}
        isRequired
        errorMessage={errors?.boxLength?.errorMessage}
        hasError={showErrors && errors?.boxLength?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.boxLength.suffix}
          onChange={(e) => {
            setBoxLength(e.target.value);
          }}
          value={boxLength}
        />
      </FormGroup>

      <FormGroup
        id="boxWidth"
        label={PAGE_CONSTANTS.boxWidth.label}
        isRequired
        errorMessage={errors?.boxWidth?.errorMessage}
        hasError={showErrors && errors?.boxWidth?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.boxWidth.suffix}
          onChange={(e) => {
            setBoxWidth(e.target.value);
          }}
          value={boxWidth}
        />
      </FormGroup>

      <FormGroup
        id="boxHeight"
        label={PAGE_CONSTANTS.boxHeight.label}
        isRequired
        errorMessage={errors?.boxHeight?.errorMessage}
        hasError={showErrors && errors?.boxHeight?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.boxHeight.suffix}
          onChange={(e) => {
            setBoxHeight(e.target.value);
          }}
          value={boxHeight}
        />
      </FormGroup>

      <FormCheckbox
        id="selectionCheckBox"
        name="isForFirstAidOnly"
        label={PAGE_CONSTANTS.isForFirstAidOnly.label}
        onChange={() => setIsForFirstAidOnly(isForFirstAidOnly === '' ? 'yes' : 'no')}
        testId="selectionCheckBox"
        value={''}
        checked={isForFirstAidOnly === 'yes'}
        margin={{ top: 'xl' }}
      />
    </>
  );
});

SuspendedWorkboxDesignDetails.displayName = 'SuspendedWorkboxDesignDetails';

SuspendedWorkboxDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
